import { Alert, Button, Form, Input, Typography } from "antd";
import BaseModal from "components/BaseModal";
import message from "components/CustomMessage";
import WizardFormV2 from "components/WizardFormV2";
import { MODAL_SIZE_MEDIUM, MODAL_SIZE_SMALL } from "constants/Layout";
import { PermissionSetMap } from "constants/RBACConstants";
import dayjs from "dayjs";
import { isNil } from "lodash";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import IntegrationsAPI from "restAPI/IntegrationsAPI";
import { hasPermission } from "utils/authService";
import { FieldTypes, nameRegexValidator } from "utils/formUtils";
import ModalUtil from "utils/modalUtil";
import { duplicateNameValidator } from "utils/validatorUtil";

import {
  CROWDSTRIKE_EDR_AUTH_TYPE,
  CROWDSTRIKE_EDR_DEFAULT_STATUS,
  CrowdStrikeEdrAPI,
  CrowdStrikeEdrConfig,
  CrowdStrikeEdrConfigRequest,
  CrowdStrikeEdrSetupInfo,
  falconBaseUrlValidator,
} from "./crowdstrikeEdrConstants";

interface CrowdStrikeEdrConfigFormProps {
  configData?: CrowdStrikeEdrConfig;
  onUpdateConfigComponent: () => void;
  configList?: CrowdStrikeEdrConfig[];
  afterClose?: () => void;
}

export default forwardRef(function CrowdStrikeEdrConfigForm(
  {
    configData,
    afterClose,
    configList = [],
    onUpdateConfigComponent,
  }: CrowdStrikeEdrConfigFormProps,
  ref,
) {
  const modalRef = useRef<any>();
  const generatedApiKeyRef = useRef<string | null>(null);
  const keyGenAttemptedRef = useRef(false);
  const [wizardForm, setWizardForm] = useState<any>();
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [apiKeyList, setApiKeyList] = useState<any[]>([]);
  const [setupInfo, setSetupInfo] = useState<CrowdStrikeEdrSetupInfo | null>(
    null,
  );

  const isEdit = !isNil(configData?.id);
  const canGenerateApiKey = hasPermission(PermissionSetMap.WRITE_TOOLS);

  useImperativeHandle(ref, () => modalRef.current);

  useEffect(() => {
    const { promise, cancel } = CrowdStrikeEdrAPI.getCloudFormationTemplate();
    promise.then((response: any) => setSetupInfo(response?.data || response));
    return cancel;
  }, []);

  useEffect(() => {
    const { promise, cancel } = IntegrationsAPI.getAllAPIKeys();
    promise.then(setApiKeyList);
    return cancel;
  }, []);

  useEffect(() => {
    if (isEdit || !setupInfo?.prefilledParameters || !wizardForm) return;
    const { IntegrationUUID } = setupInfo.prefilledParameters;
    if (IntegrationUUID) {
      wizardForm.setFieldsValue({ uuid: IntegrationUUID });
    }
  }, [setupInfo, wizardForm, isEdit]);

  const filteredConfigList = useMemo(() => {
    if (!configData?.id) return configList;
    return configList.filter((item) => item.id !== configData.id);
  }, [configList, configData?.id]);

  const falconBaseUrlOptions = useMemo(() => {
    const backendOptions = setupInfo?.prefilledParameters?.FalconBaseUrlOptions;
    if (!backendOptions) return [];
    return backendOptions
      .split(",")
      .map((url: string) => ({ label: url, value: url }));
  }, [setupInfo]);

  const initialValues = useMemo(() => {
    const defaults: Record<string, any> = {
      falconBaseUrl: "https://api.crowdstrike.com",
      keyExists: false,
      expiry: -1,
    };
    if (!configData) return defaults;
    const { authConfig, configProperties, ...rest } = configData as any;
    return { ...defaults, ...rest, ...authConfig, ...configProperties };
  }, [configData]);

  // Submit: generate API key (if needed) then save EDR configuration
  const processForm = useCallback(
    async (data: Record<string, any>) => {
      const {
        name,
        uuid,
        falconClientId,
        falconClientSecret,
        falconBaseUrl,
        keyExists,
        apiKeyName,
        expiry: expiryPeriod,
      } = data;

      // Save EDR configuration first
      const payload: Partial<CrowdStrikeEdrConfigRequest> = { name };

      if (!configData?.id) {
        payload.uuid = uuid;
        payload.status = CROWDSTRIKE_EDR_DEFAULT_STATUS;
        payload.authType = CROWDSTRIKE_EDR_AUTH_TYPE;
      }

      const authConfig: Partial<CrowdStrikeEdrConfigRequest["authConfig"]> = {};
      if (falconClientId) authConfig.falconClientId = falconClientId;
      if (falconClientSecret)
        authConfig.falconClientSecret = falconClientSecret;
      if (falconBaseUrl) authConfig.falconBaseUrl = falconBaseUrl;
      if (Object.keys(authConfig).length > 0)
        payload.authConfig =
          authConfig as CrowdStrikeEdrConfigRequest["authConfig"];
      if (falconBaseUrl) payload.configProperties = { falconBaseUrl };

      if (configData?.id) {
        await CrowdStrikeEdrAPI.updateConfiguration(configData.id, payload)
          .promise;
      } else {
        await CrowdStrikeEdrAPI.createConfiguration(
          payload as CrowdStrikeEdrConfigRequest,
        ).promise;
      }

      // Generate API key after successful save (only on create, only if user doesn't already have one)
      if (!configData?.id && !keyExists) {
        if (!canGenerateApiKey) {
          // Config is already saved — don't throw, just skip key generation
          generatedApiKeyRef.current = null;
        } else {
          keyGenAttemptedRef.current = true;
          try {
            const expiry =
              expiryPeriod === -1
                ? null
                : dayjs().add(expiryPeriod, "days").endOf("day").valueOf();
            const suffix = Date.now().toString(36);
            const tokenName = apiKeyName || `CrowdStrike EDR - ${suffix}`;
            const keyData = await IntegrationsAPI.createNewAPIKey({
              name: tokenName,
              apiTokenType: "APP_SEC_TOOL",
              expiry,
              role: null,
              productIds: [],
              accessOnAllProducts: true,
              accountLevelToken: false,
              userId: undefined,
            }).promise;
            generatedApiKeyRef.current = keyData.apiKey;
          } catch {
            // Config saved successfully — don't fail the whole submit.
            // onSuccess will show a warning instead of the key popup.
            generatedApiKeyRef.current = null;
          }
        }
      }
    },
    [configData, canGenerateApiKey],
  );

  const onSuccess = useCallback(() => {
    onUpdateConfigComponent();
    message.success("Configuration saved successfully");
    modalRef.current?.close();
    const apiKey = generatedApiKeyRef.current;
    const attempted = keyGenAttemptedRef.current;
    generatedApiKeyRef.current = null;
    keyGenAttemptedRef.current = false;
    if (apiKey) {
      ModalUtil.show({
        content: (
          <BaseModal
            modalProps={{
              width: MODAL_SIZE_SMALL,
              title: "ArmorCode API Key Generated",
              footer: null,
            }}
          >
            <Alert
              type="warning"
              message="Save this key — it won't be shown again."
              showIcon
              style={{ width: "fit-content", marginBottom: 12 }}
            />
            <Typography.Text
              copyable={{ text: apiKey }}
            >{`***************${apiKey.slice(-4)}`}</Typography.Text>
          </BaseModal>
        ),
      });
    } else if (attempted) {
      // Generation was attempted but failed — config is saved, warn the user
      message.warn(
        "Configuration saved, but API key generation failed. You can generate a key manually from API Keys settings.",
      );
    }
  }, [onUpdateConfigComponent]);

  // --- Page 1: CrowdStrike EDR Configuration ---
  const configFields = useMemo(
    () => [
      {
        render: () => (
          <Alert
            type="info"
            className="m-b-xs"
            message="Prerequisites & Setup Guide"
            description={
              <ul className="m-b-0" style={{ paddingLeft: 16 }}>
                <li>
                  Ensure <strong>Falcon LogScale</strong> is enabled and EDR
                  sensors are deployed on your endpoints.
                </li>
                <li>
                  To obtain the Client ID and Client Secret, go to{" "}
                  <strong>
                    Falcon Console &gt; Support and Resources &gt; API Clients
                    and Keys
                  </strong>
                  . Create a new API client with the following permission
                  scopes:
                  <ul style={{ paddingLeft: 16 }}>
                    <li>
                      <strong>Hosts: Read</strong>
                    </li>
                  </ul>
                </li>
                <li>
                  Note your <strong>Cloud Region</strong> from the Falcon
                  console URL.
                </li>
                <li>
                  After saving, install the{" "}
                  <strong>AC AIEM EDR Exporter</strong> Foundry App in
                  CrowdStrike and configure it with your{" "}
                  <strong>ArmorCode Instance URL</strong>,{" "}
                  <strong>API Key</strong>, and the{" "}
                  <strong>Integration UUID</strong> shown below.
                </li>
              </ul>
            }
          />
        ),
      },
      {
        name: "uuid",
        label: "UUID",
        required: true,
        render: () => (
          <Input.Group compact>
            <Form.Item name="uuid" noStyle>
              <Input
                style={{ width: "calc(100% - 80px)" }}
                readOnly
                placeholder="Auto-generated UUID"
              />
            </Form.Item>
            <Button
              onClick={() => {
                const uuid = wizardForm?.getFieldValue("uuid");
                if (uuid) {
                  navigator.clipboard.writeText(uuid);
                  message.success("UUID copied to clipboard");
                }
              }}
            >
              Copy
            </Button>
          </Input.Group>
        ),
        help: "This UUID must be entered as ac_uuid in the CrowdStrike Foundry App",
      },
      {
        name: "name",
        label: "Configuration Name",
        NoDefaultLengthRule: true,
        required: true,
        rules: [
          nameRegexValidator,
          duplicateNameValidator({ list: filteredConfigList }),
        ],
        props: {
          autoFocus: true,
          placeholder: "e.g., Production CrowdStrike EDR",
        },
      },
      {
        name: "falconClientId",
        label: "Falcon API Client ID",
        required: !isEdit,
        props: {
          placeholder: isEdit
            ? "Leave blank to keep existing"
            : "Enter your Falcon OAuth2 Client ID",
        },
        help: "Found in Falcon Console > Support and Resources > API Clients and Keys",
      },
      {
        name: "falconClientSecret",
        label: "Falcon API Client Secret",
        type: FieldTypes.PASSWORD,
        required: !isEdit,
        props: {
          placeholder: isEdit
            ? "Leave blank to keep existing"
            : "Enter your Falcon OAuth2 Client Secret",
        },
      },
      {
        name: "falconBaseUrl",
        label: "Falcon Cloud Region",
        type: FieldTypes.SELECT,
        required: !isEdit,
        options: falconBaseUrlOptions,
        rules: [falconBaseUrlValidator],
        props: {
          placeholder: "Select your Falcon cloud region",
          showSearch: true,
          filterOption: (input: string, option: { label: string }) =>
            option?.label?.toLowerCase().includes(input.toLowerCase()),
        },
      },
    ],
    [filteredConfigList, isEdit, falconBaseUrlOptions, wizardForm],
  );

  // --- Page 2: ArmorCode API Key ---
  const apiKeyFields = useMemo(
    () => [
      {
        render: () => (
          <Alert
            message="Why do you need an ArmorCode API key?"
            description="The API key is used by the CrowdStrike Foundry App to authenticate and send EDR data to ArmorCode. It will be generated when you click Submit."
            showIcon
            className="m-b-sm"
          />
        ),
      },
      ...(!canGenerateApiKey
        ? [
            {
              render: () => (
                <Alert
                  type="warning"
                  message="You don't have permission to generate API keys. Toggle 'I already have an API key' or contact your admin."
                  showIcon
                  className="m-b-sm"
                />
              ),
            },
          ]
        : []),
      {
        name: "keyExists",
        label: "I already have an ArmorCode API key",
        type: FieldTypes.BOOLEAN,
        inline: true,
      },
      {
        name: "apiKeyName",
        label: (
          <>
            {!formData.keyExists ? (
              <span style={{ color: "red", fontSize: 10, marginRight: "3px" }}>
                *
              </span>
            ) : (
              ""
            )}
            API Key Name
          </>
        ),
        placeholder: "Enter a name for the API key",
        disabled: formData.keyExists || !canGenerateApiKey,
        rules: [
          nameRegexValidator({ min: 3 }),
          {
            validator: (_: any, value: string) => {
              if (!value && !formData.keyExists) {
                return Promise.reject("API Key Name is required");
              }
              if (value && !formData.keyExists) {
                for (const item of apiKeyList) {
                  if (item.name === value) {
                    return Promise.reject("API Key Name already exists");
                  }
                }
              }
              return Promise.resolve();
            },
          },
        ],
      },
      {
        name: "expiry",
        label: "Expiry",
        disabled: formData.keyExists || !canGenerateApiKey,
        type: FieldTypes.SELECT,
        options: [
          { label: "Never", value: -1 },
          { label: "3 months", value: 90 },
          { label: "6 months", value: 180 },
          { label: "1 year", value: 365 },
        ],
        rules: [
          {
            required: !formData.keyExists,
            message: "Expiry is required",
          },
        ],
      },
    ],
    [formData.keyExists, canGenerateApiKey, apiKeyList],
  );

  const pages = useMemo(() => {
    if (isEdit) {
      return [{ name: "EDR Configuration", fields: configFields }];
    }
    return [
      { name: "EDR Configuration", fields: configFields },
      { name: "ArmorCode API Key", fields: apiKeyFields },
    ];
  }, [isEdit, apiKeyFields, configFields]);

  return (
    <BaseModal
      modalProps={{
        footer: null,
        width: MODAL_SIZE_MEDIUM,
        style: { top: 80 },
        title: isEdit
          ? "Edit CrowdStrike EDR Configuration"
          : "Configure CrowdStrike EDR",
      }}
      ref={modalRef}
      afterClose={() => afterClose?.()}
    >
      <div className="crowdstrike-edr-wizard">
        <WizardFormV2
          validateOnBackwardStep={false}
          onCancel={() => modalRef.current?.close?.()}
          pages={pages}
          initialValues={initialValues}
          onChange={(data: any) =>
            setFormData((prev: any) => ({ ...prev, ...data }))
          }
          formRef={setWizardForm}
          api={processForm}
          onSuccess={onSuccess}
          isEdit={isEdit}
        />
      </div>
    </BaseModal>
  );
});
